import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast, Toaster } from "sonner";
import {
    ArrowLeft, Star, MapPin, Clock, Heart, ExternalLink, Map, Loader2, Copy, Check,
} from "lucide-react";

const TYPE_LABELS = { hotel: "Hotel", restaurant: "Restaurant", activity: "Experience" };

function buildBookingLink(type, destination, name) {
    // Simpler, more reliable Booking.com search URL (works without affiliate id).
    const q = encodeURIComponent(`${name} ${destination}`);
    if (type === "hotel") {
        return {
            label: "Book on Booking.com",
            href: `https://www.booking.com/search.html?ss=${q}&group_adults=2&no_rooms=1`,
        };
    }
    if (type === "restaurant") {
        return {
            label: "Reserve on OpenTable",
            href: `https://www.opentable.com/s?term=${q}`,
        };
    }
    return {
        label: "Browse on GetYourGuide",
        href: `https://www.getyourguide.com/s/?q=${q}`,
    };
}

function buildGoogleMapsLink(destination, name) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${name} ${destination}`)}`;
}

function buildGoogleMapsEmbed(destination, name) {
    // Google Maps free embed — no API key required for public search.
    return `https://www.google.com/maps?q=${encodeURIComponent(`${name} ${destination}`)}&output=embed&z=14`;
}

function buildAppleMapsLink(destination, name) {
    return `https://maps.apple.com/?q=${encodeURIComponent(`${name} ${destination}`)}`;
}

function buildOpenStreetMapLink(destination, name) {
    return `https://www.openstreetmap.org/search?query=${encodeURIComponent(`${name} ${destination}`)}`;
}

/** Reliable popup opener that works inside sandboxed iframes (Emergent preview).
 *  Falls back to current-tab navigation if window.open is blocked. */
function safeOpen(url) {
    try {
        const win = window.open(url, "_blank", "noopener,noreferrer");
        if (!win || win.closed || typeof win.closed === "undefined") {
            // popup blocked — break out of the iframe to the top tab
            window.top.location.href = url;
        }
    } catch (_e) {
        window.top.location.href = url;
    }
}

export default function PlaceDetail() {
    const [params] = useSearchParams();
    const location = useLocation();
    const nav = useNavigate();
    const { user } = useAuth();

    const destination = params.get("destination") || "";
    const type = params.get("type") || "hotel";
    const name = params.get("name") || "";

    const [item, setItem] = useState(location.state?.item || null);
    const [loading, setLoading] = useState(!item);
    const [error, setError] = useState("");
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (item) return;
        if (!destination || !name) { setLoading(false); setError("Missing place reference"); return; }
        let aborted = false;
        setLoading(true);
        api.post("/search", { destination, days: 5, travelers: 2, budget_level: "mid" })
            .then(({ data }) => {
                if (aborted) return;
                const list = type === "hotel" ? data.hotels : type === "restaurant" ? data.restaurants : data.activities;
                const found = (list || []).find((it) => it.name === name);
                if (!found) setError("Place not found in this destination. It may have changed.");
                else setItem(found);
            })
            .catch((e) => !aborted && setError(formatApiError(e.response?.data?.detail) || e.message))
            .finally(() => !aborted && setLoading(false));
        return () => { aborted = true; };
    }, [destination, type, name, item]);

    const booking = useMemo(
        () => (item ? buildBookingLink(type, destination, item.name) : null),
        [item, type, destination],
    );
    const mapsLink = useMemo(() => (item ? buildGoogleMapsLink(destination, item.name) : ""), [item, destination]);
    const mapsEmbed = useMemo(() => (item ? buildGoogleMapsEmbed(destination, item.name) : ""), [item, destination]);
    const appleLink = useMemo(() => (item ? buildAppleMapsLink(destination, item.name) : ""), [item, destination]);
    const osmLink = useMemo(() => (item ? buildOpenStreetMapLink(destination, item.name) : ""), [item, destination]);

    const saveFav = async () => {
        if (!user || !user._id) { toast.error("Please sign in to save favourites"); return; }
        try {
            await api.post("/favorites", {
                item_type: type, destination, name: item.name, payload: item,
            });
            toast.success("Saved to your favourites");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const copyLink = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            toast.success("Link copied to clipboard");
            setTimeout(() => setCopied(false), 2000);
        } catch (_e) {
            toast.error("Couldn't copy — long-press to copy manually");
        }
    };

    if (loading) {
        return (
            <div className="App">
                <Navbar />
                <div className="mx-auto mt-24 grid max-w-md place-items-center text-center" data-testid="place-loading">
                    <Loader2 size={32} className="animate-spin text-terracotta" />
                    <p className="mt-4 font-display text-2xl text-ink">Loading place…</p>
                </div>
            </div>
        );
    }

    if (error || !item) {
        return (
            <div className="App">
                <Navbar />
                <div className="mx-auto mt-24 max-w-xl rounded-3xl border border-destructive/30 bg-destructive/5 p-6 text-center">
                    <p className="font-display text-2xl text-destructive">{error || "Place not found"}</p>
                    <button onClick={() => nav(-1)} className="btn-ghost mt-4">Go back</button>
                </div>
            </div>
        );
    }

    const ratingValue = Number(item.rating || 0).toFixed(1);

    return (
        <div className="App">
            <Toaster position="top-center" richColors closeButton />
            <Navbar />

            <section className="relative mt-4">
                <div className="relative h-[55vh] overflow-hidden">
                    <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/15 to-transparent" />
                </div>

                <div className="mx-auto -mt-32 max-w-5xl px-6">
                    <button onClick={() => nav(-1)} data-testid="place-back" className="pill bg-white/90 text-ink hover:bg-white">
                        <ArrowLeft size={14} className="mr-2" /> Back
                    </button>

                    <div className="mt-4 grid gap-8 md:grid-cols-[1.6fr_1fr]">
                        <div className="glass rounded-3xl p-6 md:p-10">
                            <span className="overline">{TYPE_LABELS[type]} in {destination}</span>
                            <h1 data-testid="place-title" className="mt-2 font-display text-4xl text-ink md:text-6xl">{item.name}</h1>

                            <div className="mt-5 flex flex-wrap items-center gap-3 text-sm">
                                <span className="flex items-center gap-1 rounded-full bg-terracotta/10 px-3 py-1 text-terracotta">
                                    <Star size={14} className="fill-terracotta" /> {ratingValue}
                                </span>
                                {item.stars && (<span className="rounded-full bg-ink/8 px-3 py-1 text-ink">{"★".repeat(item.stars)}</span>)}
                                {item.price_range && (<span className="rounded-full bg-jade/10 px-3 py-1 text-jade">{item.price_range}</span>)}
                                {item.cuisine && (<span className="rounded-full bg-jade/10 px-3 py-1 text-jade uppercase tracking-wider text-xs">{item.cuisine}</span>)}
                                {item.category && (<span className="rounded-full bg-jade/10 px-3 py-1 text-jade uppercase tracking-wider text-xs">{item.category}</span>)}
                                {item.neighborhood && (
                                    <span className="flex items-center gap-1 rounded-full bg-white px-3 py-1 text-moss">
                                        <MapPin size={12} /> {item.neighborhood}
                                    </span>
                                )}
                                {item.duration_hours && (
                                    <span className="flex items-center gap-1 rounded-full bg-white px-3 py-1 text-moss">
                                        <Clock size={12} /> {item.duration_hours}h
                                    </span>
                                )}
                            </div>

                            {(item.highlight || item.description) && (
                                <p className="mt-6 max-w-2xl text-lg leading-relaxed text-moss">{item.highlight || item.description}</p>
                            )}

                            {item.must_try && (
                                <div className="mt-6 rounded-2xl bg-terracotta/5 p-4">
                                    <span className="overline">must try</span>
                                    <p className="mt-1 font-display text-xl text-ink">{item.must_try}</p>
                                </div>
                            )}

                            {item.amenities && (
                                <div className="mt-6">
                                    <p className="overline">amenities</p>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {item.amenities.map((a, i) => (
                                            <span key={i} className="rounded-full bg-white px-3 py-1 text-sm text-ink ring-1 ring-jade/10">{a}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <aside className="space-y-4 md:sticky md:top-24">
                            <div className="rounded-3xl bg-white p-6 ring-1 ring-jade/10">
                                <p className="overline">price</p>
                                <p className="mt-1 font-display text-4xl text-ink">
                                    ${item.price_per_night_usd ?? item.price_usd ?? "—"}
                                    {item.price_per_night_usd && <span className="text-sm font-sans text-moss">/night</span>}
                                </p>
                                <div className="mt-5 space-y-3">
                                    <button
                                        onClick={() => safeOpen(booking.href)}
                                        data-testid="place-book"
                                        data-href={booking.href}
                                        className="btn-primary w-full justify-center"
                                    >
                                        <ExternalLink size={14} className="mr-2" /> {booking.label}
                                    </button>
                                    <button
                                        onClick={() => safeOpen(mapsLink)}
                                        data-testid="place-maps"
                                        data-href={mapsLink}
                                        className="btn-secondary w-full justify-center"
                                    >
                                        <Map size={14} className="mr-2" /> Open in Google Maps
                                    </button>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button
                                            onClick={() => safeOpen(appleLink)}
                                            data-testid="place-apple-maps"
                                            data-href={appleLink}
                                            className="pill border border-ink/15 text-ink hover:bg-ink/5 justify-center !px-3 !py-2 text-xs"
                                        >
                                            Apple Maps
                                        </button>
                                        <button
                                            onClick={() => safeOpen(osmLink)}
                                            data-testid="place-osm"
                                            data-href={osmLink}
                                            className="pill border border-ink/15 text-ink hover:bg-ink/5 justify-center !px-3 !py-2 text-xs"
                                        >
                                            OpenStreetMap
                                        </button>
                                    </div>
                                    <button
                                        onClick={() => copyLink(booking.href)}
                                        data-testid="place-copy-booking"
                                        className="btn-ghost w-full justify-center text-xs"
                                    >
                                        {copied ? <Check size={12} className="mr-2" /> : <Copy size={12} className="mr-2" />}
                                        Copy {type === "hotel" ? "booking" : "reservation"} link
                                    </button>
                                    <button onClick={saveFav} data-testid="place-save" className="btn-ghost w-full justify-center">
                                        <Heart size={14} className="mr-2" /> Save to favourites
                                    </button>
                                </div>
                            </div>
                            <Link to={`/explore?destination=${encodeURIComponent(destination)}&days=5&travelers=2&budget_level=mid`}
                                  className="block rounded-3xl bg-jade p-5 text-sand transition hover:-translate-y-0.5">
                                <p className="overline !text-sand/80">more in</p>
                                <p className="mt-1 font-display text-2xl">{destination}</p>
                                <p className="mt-2 text-sm text-sand/80">See all hotels, restaurants & activities →</p>
                            </Link>
                        </aside>
                    </div>

                    {/* Embedded Google Maps — works inside sandbox previews, no API key required */}
                    <div className="mt-12 overflow-hidden rounded-3xl bg-white ring-1 ring-jade/10 shadow-sm" data-testid="place-map-embed">
                        <div className="flex items-center justify-between border-b border-jade/10 px-5 py-3">
                            <div className="flex items-center gap-2">
                                <span className="grid h-8 w-8 place-items-center rounded-full bg-terracotta/10 text-terracotta">
                                    <MapPin size={14} />
                                </span>
                                <div>
                                    <p className="font-display text-lg leading-none text-ink">Location</p>
                                    <p className="text-xs text-moss">{item.name} · {destination}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => safeOpen(mapsLink)}
                                className="hidden text-xs text-jade hover:text-terracotta sm:inline-flex items-center gap-1"
                            >
                                Open in Google Maps <ExternalLink size={11} />
                            </button>
                        </div>
                        <iframe
                            title={`Map of ${item.name}`}
                            src={mapsEmbed}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            allow="geolocation"
                            className="h-[420px] w-full border-0"
                            data-testid="place-map-iframe"
                        />
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
